import React, { useState, useEffect, Fragment } from 'react';
import { Input, Table, message, Modal, Tag, Button, Space, Tooltip, Form, Tree } from "antd";
import { ReloadOutlined, EyeOutlined, DeleteOutlined, StopOutlined, BranchesOutlined } from '@ant-design/icons';
import useRequest from '@/utils/useRequest';
import useWscTableList from '@/component/UseWscTableList';
import useWscCustomColumns from '@/component/UseWscCustomColumns';
import ColumnSettings from '@/component/ColumnSettings';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// Token树的样式
const tokenTreeStyle = `
  .token-tree .ant-tree-node-content-wrapper {
    padding: 8px 12px;
    border-radius: 6px;
    margin: 2px 0;
    transition: all 0.2s;
  }
  .token-tree .ant-tree-node-content-wrapper:hover {
    background-color: #f0f9ff;
  }
  .token-tree .ant-tree-title {
    width: 100%;
  }
  .token-tree .ant-tree-switcher {
    color: #1890ff;
  }
`;

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { Search } = Input;

const initParams = {
  pagination: {
    current: 1,
    pageSize: 10
  },
  filters: {},
  sorter: null
};

const BpmnInstances = () => {
  // 添加样式到页面
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = tokenTreeStyle;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  const { request } = useRequest();
  const { list, isLoading, pagination, setPage, setFilter, filters, setSorter, Refresh } = useWscTableList({
    url: "http://127.0.0.1:8080/api/v1/processes/instances", 
    initParams: initParams
  });
  const { Refresh: refreshColumns, colsettings } = useWscCustomColumns({ key: "bpmn_instances" });
  const [columns, setColumns] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  // 默认列配置
  const defaultColumns = [
    {
      title: '实例ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      ellipsis: true,
      visible: true
    },
    {
      title: '流程名称',
      dataIndex: 'process_definition_name',
      key: 'process_definition_name',
      ellipsis: true,
      visible: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '流程Key',
      dataIndex: 'process_definition_key',
      key: 'process_definition_key',
      ellipsis: true,
      visible: true
    },
    {
      title: '业务键',
      dataIndex: 'business_key',
      key: 'business_key',
      width: 150,
      ellipsis: true,
      visible: true,
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      visible: true,
      render: (status, record) => {
        if (status === 'Completed' || record.ended_at) {
          return <Tag color="default">已完成</Tag>;
        }
        if (status === 'Suspended') {
          return <Tag color="orange">挂起</Tag>;
        }
        if (status === 'Active') {
          return <Tag color="green">运行中</Tag>;
        }
        return <Tag color="red">未知</Tag>;
      }
    },
    {
      title: '启动时间',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      visible: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '结束时间',
      dataIndex: 'ended_at',
      key: 'ended_at',
      width: 160,
      visible: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '启动用户',
      dataIndex: 'started_by',
      key: 'started_by',
      width: 120,
      visible: true,
      render: (text) => text || '-'
    },
    {
      title: '持续时间',
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
      visible: true,
      render: (duration, record) => {
        if (record.started_at && !record.ended_at) {
          // 如果流程还在运行中，计算当前持续时间
          const now = dayjs();
          const start = dayjs(record.started_at);
          const diff = now.diff(start);
          return dayjs.duration(diff).humanize();
        }
        if (record.started_at && record.ended_at) {
          // 如果流程已结束，计算总持续时间
          const start = dayjs(record.started_at);
          const end = dayjs(record.ended_at);
          const diff = end.diff(start);
          return dayjs.duration(diff).humanize();
        }
        return '-';
      }
    }
  ];

  const ExtendColumns = [
    {
      title: <div>操作 <ColumnSettings topic="bpmn_instances" colsettings={colsettings} list={list} onFinish={refreshColumns} /></div>,
      dataIndex: 'id',
      key: 'id',
      width: 150,
      fixed: 'right',
      render: (id, record) => (
        <Fragment>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {!record.ended_at && record.status === 'Active' && (
            <Button 
              type="link" 
              danger
              icon={<StopOutlined />} 
              onClick={() => handleTerminate(record)}
            >
              终止
            </Button>
          )}
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Fragment>
      )
    }
  ];

  // 初始化加载
  useEffect(() => {
    // 使用自定义列设置或默认列设置
    const baseColumns = colsettings && colsettings.length > 0 
      ? colsettings.filter(item => item.visible) 
      : defaultColumns.filter(item => item.visible);
    
    let cols = baseColumns.concat(ExtendColumns);
    setColumns(cols);
  }, [colsettings]);

  // 处理表格变化（分页、排序、筛选）
  const handleTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current);
    setFilter({ ...filters, ..._filters });
    setSorter(sorter);
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    setFilter({ ...filters, keywords: value });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchKeyword('');
    setFilter(initParams.filters);
    setPage(1);
  };

  // 刷新列表
  const handleRefresh = () => {
    Refresh();
  };

  // 查看详情
  const handleViewDetail = async (record) => {
    try {
      const result = await request(
        `http://127.0.0.1:8080/api/v1/processes/instances/${record.id}/detail`,
        "GET"
      );
      
      if (result && result.success && result.data) {
        const { basic_info, token_statistics, execution_history, current_activities, performance_metrics, token_tree_snapshot } = result.data;
        
        // 构建Token树形数据
        const buildTokenTree = (nodes) => {
          if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return [];
          
          // 创建节点映射
          const nodeMap = new Map();
          nodes.forEach(node => {
            const tokenData = node.token || node;
            const statusColor = {
              'Active': 'green',
              'Completed': 'blue', 
              'Suspended': 'orange',
              'active': 'green',
              'completed': 'blue',
              'waiting': 'orange',
              'failed': 'red'
            }[tokenData.status] || 'default';
            
            const statusText = {
              'Active': '活跃',
              'Completed': '已完成',
              'Suspended': '挂起',
              'active': '活跃',
              'completed': '已完成',
              'waiting': '等待中',
              'failed': '失败'
            }[tokenData.status] || tokenData.status;
            
            nodeMap.set(tokenData.id, {
              key: tokenData.id,
              title: (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <BranchesOutlined className="mr-2" />
                    <span className="font-medium">
                      {tokenData.current_flow_element || `Token ${tokenData.id}`}
                    </span>
                    <Tag color={statusColor} className="ml-2">
                      {statusText}
                    </Tag>
                  </div>
                  <div className="text-xs text-gray-500">
                    {tokenData.token_type && (
                      <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-600 rounded">
                        {tokenData.token_type}
                      </span>
                    )}
                    {tokenData.start_time && (
                      <span>开始: {dayjs(tokenData.start_time).format('HH:mm:ss')}</span>
                    )}
                    {tokenData.end_time && (
                      <span className="ml-2">结束: {dayjs(tokenData.end_time).format('HH:mm:ss')}</span>
                    )}
                  </div>
                </div>
              ),
              children: [],
              tokenData: tokenData,
              parentId: node.parent_token_id || tokenData.parent_token_id,
              childrenCount: node.children_count || 0
            });
          });
          
          // 构建树形结构
          const rootNodes = [];
          nodeMap.forEach(node => {
            if (node.parentId === null || node.parentId === undefined) {
              rootNodes.push(node);
            } else {
              const parent = nodeMap.get(node.parentId);
              if (parent) {
                parent.children.push(node);
              } else {
                // 如果找不到父节点，也作为根节点处理
                rootNodes.push(node);
              }
            }
          });
          
          return rootNodes;
        };
        
        const tokenTreeData = buildTokenTree(token_tree_snapshot?.nodes || []);
        
        Modal.info({
          title: (
            <div className="flex items-center">
              <EyeOutlined className="mr-2 text-blue-500" />
              <span>流程实例详情</span>
            </div>
          ),
          width: 900,
          content: (
            <div className="space-y-6">
              {/* 基本信息卡片 */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  基本信息
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">实例ID</span>
                    </div>
                    <div className="font-medium text-gray-800">{basic_info.id}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">流程名称</span>
                    </div>
                    <div className="font-medium text-gray-800">{basic_info.process_definition_name || '-'}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">流程Key</span>
                    </div>
                    <div className="font-medium text-gray-800">{basic_info.process_definition_key || '-'}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">状态</span>
                    </div>
                    <div className="font-medium">
                      <Tag color={basic_info.status === 'Active' ? 'green' : basic_info.status === 'Suspended' ? 'orange' : 'default'}>
                        {basic_info.status === 'Active' ? '运行中' : basic_info.status === 'Suspended' ? '挂起' : basic_info.status === 'Completed' ? '已完成' : '已终止'}
                      </Tag>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">启动时间</span>
                    </div>
                    <div className="font-medium text-gray-800">
                      {basic_info.started_at ? dayjs(basic_info.started_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">结束时间</span>
                    </div>
                    <div className="font-medium text-gray-800">
                      {basic_info.ended_at ? dayjs(basic_info.ended_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Token统计信息 */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  Token统计与树形结构
                </h3>
                
                {/* 统计概览 */}
                <div className="grid grid-cols-5 gap-3 mb-4">
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="text-2xl font-bold text-blue-600">{token_statistics.total_tokens}</div>
                    <div className="text-sm text-gray-600">总数</div>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="text-2xl font-bold text-green-600">{token_statistics.active_tokens}</div>
                    <div className="text-sm text-gray-600">活跃</div>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="text-2xl font-bold text-gray-600">{token_statistics.completed_tokens}</div>
                    <div className="text-sm text-gray-600">已完成</div>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="text-2xl font-bold text-orange-600">{token_statistics.waiting_tokens}</div>
                    <div className="text-sm text-gray-600">等待中</div>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="text-2xl font-bold text-red-600">{token_statistics.failed_tokens || 0}</div>
                    <div className="text-sm text-gray-600">失败</div>
                  </div>
                </div>
                
                {/* Token树形结构 */}
                <div className="bg-white p-4 rounded border">
                  <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                    <BranchesOutlined className="mr-2 text-green-500" />
                    Token执行树
                  </h4>
                  {tokenTreeData.length > 0 ? (
                    <Tree
                      treeData={tokenTreeData}
                      defaultExpandAll
                      showLine={{ showLeafIcon: false }}
                      className="token-tree"
                    />
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      暂无Token树形数据
                    </div>
                  )}
                </div>
              </div>

              {/* 性能指标 */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                  性能指标
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">总执行时间</span>
                    </div>
                    <div className="font-medium text-gray-800">
                      {performance_metrics.total_execution_time_ms ? 
                        dayjs.duration(performance_metrics.total_execution_time_ms).humanize() : '-'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">完成进度</span>
                    </div>
                    <div className="font-medium text-gray-800">
                      {performance_metrics.completion_percentage.toFixed(1)}% 
                      ({performance_metrics.completed_nodes_count}/{performance_metrics.total_nodes_count})
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center mb-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">最大等待时间</span>
                    </div>
                    <div className="font-medium text-gray-800">
                      {performance_metrics.max_waiting_time_ms ? 
                        dayjs.duration(performance_metrics.max_waiting_time_ms).humanize() : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 当前活动 */}
              {current_activities && current_activities.length > 0 && (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                    当前活动
                  </h3>
                  <div className="space-y-2">
                    {current_activities.map((activity, index) => (
                      <div key={index} className="bg-white p-3 rounded border flex justify-between items-center">
                        <div>
                          <div className="font-medium text-gray-800">{activity.node_name || activity.node_id}</div>
                          <div className="text-sm text-gray-600">类型: {activity.node_type} | Token: {activity.token_id}</div>
                        </div>
                        <div className="text-right">
                          <Tag color={activity.token_status === 'active' ? 'green' : 'orange'}>
                            {activity.token_status === 'active' ? '活跃' : '等待'}
                          </Tag>
                          <div className="text-sm text-gray-600 mt-1">
                            等待: {activity.waiting_time_ms ? dayjs.duration(activity.waiting_time_ms).humanize() : '-'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 执行历史 */}
              {execution_history && execution_history.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                    执行历史
                  </h3>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {execution_history.map((history, index) => (
                      <div key={index} className="bg-white p-3 rounded border">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-gray-800">{history.activity_name || history.activity_id}</div>
                            <div className="text-sm text-gray-600">类型: {history.activity_type} | Token: {history.token_id}</div>
                          </div>
                          <Tag color={history.status === 'Active' ? 'green' : history.status === 'Completed' ? 'blue' : 'default'}>
                            {history.status === 'Active' ? '活跃' : history.status === 'Completed' ? '已完成' : history.status}
                          </Tag>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <div>开始: {history.start_time ? dayjs(history.start_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</div>
                          {history.end_time && (
                            <div>结束: {dayjs(history.end_time).format('YYYY-MM-DD HH:mm:ss')}</div>
                          )}
                          {history.duration_ms && (
                            <div>耗时: {dayjs.duration(history.duration_ms).humanize()}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ),
          okText: '关闭'
        });
      } else {
        message.error(result?.message || '获取流程实例详情失败');
      }
    } catch (error) {
      console.error('获取流程实例详情失败:', error);
      message.error('获取流程实例详情失败');
    }
  };

  // 终止实例
  const handleTerminate = (record) => {
    Modal.confirm({
      title: '确认终止',
      content: `您确定要终止流程实例 "${record.id}" 吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await request(
            `http://127.0.0.1:8080/api/v1/processes/instances/${record.id}/terminate`,
            "POST",
            "json",
            { reason: '用户手动终止' }
          );
          if (result && result.success !== false) {
            message.success('流程实例已终止');
            handleRefresh();
          } else {
            message.error(result?.message || '终止失败');
          }
        } catch (error) {
          console.error('终止流程实例失败:', error);
          message.error('终止流程实例失败');
        }
      }
    });
  };

  // 删除实例
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `您确定要删除流程实例 "${record.id}" 吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await request(
            `http://127.0.0.1:8080/api/v1/processes/instances/${record.id}/delete`,
            "POST",
            "json",
            {}
          );
          if (result && result.success !== false) {
            message.success('流程实例已删除');
            handleRefresh();
          } else {
            message.error(result?.message || '删除失败');
          }
        } catch (error) {
          console.error('删除流程实例失败:', error);
          message.error('删除流程实例失败');
        }
      }
    });
  };

  return (
    <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'scroll' }} className="bg-white pl-4 pb-4">
      <div className="flex flex-row mb-4 items-center justify-between">
        <div className="flex flex-row items-center mt-4">
          <span className="text-base text-gray-800 font-bold mr-4">查询</span>
          <div>
            <Search 
              placeholder="请输入实例ID或业务键" 
              onSearch={handleSearch}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              enterButton 
              style={{ width: 300 }}
            />
          </div>
        </div>
        <div className="flex flex-row items-center mt-4 mr-4">
          <Button onClick={handleReset} className="mr-2">
            重置
          </Button>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={isLoading}
          >
            刷新
          </Button>
        </div>
      </div>

      <Table
        rowKey="id"
        loading={isLoading}
        bordered
        dataSource={list}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default BpmnInstances;